import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import axios from 'axios';
import {
  Search, MapPin, Star, Users as UsersIcon, ChevronRight, Wifi, Coffee, Car,
  Hotel as HotelIcon, Palmtree, Home, Tent, Quote, Sparkles, TrendingUp, Award,
  BedDouble, Building2, Trees, Bed, Users,
} from 'lucide-react';
import { toast } from 'sonner';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectGroup, SelectLabel } from '../components/ui/select';
import { getAllCities, getAreasByCity, getCitiesGroupedByState, getAreasGroupedByState } from '../data/indianCities';
import { DatePicker } from '../components/DatePicker';
import { ImageGallery } from '../components/ImageGallery';
import { AreaSearchInput } from '../components/AreaSearchInput';
import { GuestPicker } from '../components/GuestPicker';
import { HotelListCard } from '../components/home/HotelListCard';
import { TopHotelCard } from '../components/home/TopHotelCard';
import { PriceDropSubscribe } from '../components/home/PriceDropSubscribe';
import { WalletHeroStrip } from '../components/home/WalletHeroStrip';
import { TRENDING_DESTINATIONS, PROPERTY_TYPES, TESTIMONIALS, INSIGHTS } from '../data/landingContent';
import { useSiteContent } from '../hooks/useSiteContent';
import { dangerousSafeHtml } from '../utils/sanitizeHtml';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PROPERTY_ICONS = { Hotel: HotelIcon, Palmtree, Home, Tent, BedDouble, Building2, Trees, Bed, Users };

const AMENITY_FILTERS = [
  { id: 'Free WiFi', label: 'Free WiFi', icon: <Wifi className="h-4 w-4" /> },
  { id: 'Free Breakfast', label: 'Free Breakfast', icon: <Coffee className="h-4 w-4" /> },
  { id: 'Free Parking', label: 'Free Parking', icon: <Car className="h-4 w-4" /> },
  { id: 'Swimming Pool', label: 'Pool' },
  { id: 'Gym', label: 'Gym' },
  { id: 'Spa', label: 'Spa' },
];

export const HomePage = () => {
  const { t } = useTranslation();
  const { sections: homeContent } = useSiteContent('homepage');
  const { sections: ptContent } = useSiteContent('property-types');
  const propertyTypes = Array.isArray(ptContent.items) && ptContent.items.length > 0
    ? ptContent.items.filter((it) => it.enabled !== false)
    : PROPERTY_TYPES;
  const [hotels, setHotels] = useState([]);
  const [filters, setFilters] = useState({ city: '', area: '', checkIn: '', checkOut: '', adults: 2, children: 2 });
  const [selectedAmenities, setSelectedAmenities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [galleryImages, setGalleryImages] = useState([]);
  const [galleryHotelName, setGalleryHotelName] = useState('');
  const [isGalleryOpen, setIsGalleryOpen] = useState(false);
  const resultsRef = React.useRef(null);
  const navigate = useNavigate();

  const fetchHotels = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.city) params.append('city_area', filters.city);
      if (filters.area) params.append('city_area', filters.area);
      if (selectedAmenities.length > 0) params.append('amenities', selectedAmenities.join(','));
      const { data } = await axios.get(`${API}/guest/hotels?${params.toString()}`);
      setHotels(data);
    } catch (error) {
      toast.error('Failed to load hotels');
    } finally {
      setLoading(false);
    }
  }, [filters.city, filters.area, selectedAmenities]);

  useEffect(() => {
    fetchHotels();
  }, [fetchHotels]);

  const toggleAmenity = (id) =>
    setSelectedAmenities((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const openGallery = (images, name) => {
    setGalleryImages(images || []);
    setGalleryHotelName(name);
    setIsGalleryOpen(true);
  };

  const handleSearch = () => {
    fetchHotels();
    // After a brief delay (for state + render), scroll to the city-results section.
    setTimeout(() => {
      if (resultsRef.current) {
        resultsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 250);
  };
  const areas = filters.city ? getAreasByCity(filters.city) : [];

  // Build a hotel URL preserving selected dates + guests so the details page can autofill the booking form.
  const buildHotelUrl = useCallback((hotelId) => {
    const params = new URLSearchParams();
    if (filters.checkIn) params.set('check_in', filters.checkIn);
    if (filters.checkOut) params.set('check_out', filters.checkOut);
    if (filters.adults) params.set('adults', String(filters.adults));
    if (filters.children !== undefined) params.set('children', String(filters.children));
    const qs = params.toString();
    return `/hotels/${hotelId}${qs ? `?${qs}` : ''}`;
  }, [filters.checkIn, filters.checkOut, filters.adults, filters.children]);

  const shareHotel = (e, hotel) => {
    e.stopPropagation();
    const url = `${window.location.origin}${buildHotelUrl(hotel._id)}`;
    const text = `${hotel.hotel_name} on localliink.com — ${url}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank', 'noopener,noreferrer');
  };

  // Top Hotels — featured ranking (commission_rate × rating)
  const topHotels = [...hotels]
    .sort((a, b) => (b.commission_rate || 0) * (b.rating || 0) - (a.commission_rate || 0) * (a.rating || 0))
    .slice(0, 8);

  return (
    <div className="min-h-screen bg-white" data-testid="home-page">
      {/* ========== HERO ========== */}
      <section className="relative pt-12 pb-20 bg-gradient-to-b from-[#FFF5F8] via-white to-white overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-20 -right-20 w-96 h-96 bg-[#FF4D8A] rounded-full opacity-10 blur-3xl"></div>
          <div className="absolute top-40 -left-32 w-80 h-80 bg-[#FF0059] rounded-full opacity-5 blur-3xl"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black tracking-tight text-black mb-4 leading-[1.05]" data-testid="hero-title">
            {homeContent.hero_title || t('hero.title')}
          </h1>
          {homeContent.hero_tagline_html ? (
            <div
              className="text-lg sm:text-xl text-gray-600 max-w-2xl mx-auto mb-10 prose prose-sm prose-p:my-0"
              dangerouslySetInnerHTML={dangerousSafeHtml(homeContent.hero_tagline_html)}
              data-testid="hero-tagline-html"
            />
          ) : (
            <p className="text-lg sm:text-xl text-gray-600 max-w-2xl mx-auto mb-10">
              <Trans
                i18nKey="hero.subtitle"
                components={{ strong: <span className="text-[#FF0059] font-bold" /> }}
              />
            </p>
          )}

          {/* Hero Search Bar — TripAdvisor pill */}
          <div className="bg-white shadow-[0_8px_40px_rgba(0,0,0,0.12)] rounded-3xl md:rounded-full p-2 max-w-5xl mx-auto border border-gray-200" data-testid="hero-search-bar">
            <div className="flex flex-col md:flex-row md:items-stretch">
              {/* Where */}
              <div className="flex-1 px-5 py-3 md:py-2 hover:bg-gray-50 rounded-2xl md:rounded-full transition-colors min-w-0">
                <label className="text-[10px] font-bold uppercase tracking-wider text-gray-500 block mb-0.5">{t('hero.where')}</label>
                <AreaSearchInput
                  value={filters.city}
                  placeholder={homeContent.search_placeholder || t('hero.whereLabel')}
                  onSelect={({ city }) => setFilters({ ...filters, city, area: '' })}
                  onClear={() => setFilters({ ...filters, city: '', area: '' })}
                />
              </div>
              <div className="hidden md:block w-px self-stretch bg-gray-200 my-2" />

              {/* Check-in */}
              <div className="flex-1 px-5 py-3 md:py-2 hover:bg-gray-50 rounded-2xl md:rounded-full transition-colors">
                <label className="text-[10px] font-bold uppercase tracking-wider text-gray-500 block mb-0.5">{t('hero.checkIn')}</label>
                <DatePicker
                  value={filters.checkIn}
                  onChange={(iso) => setFilters({ ...filters, checkIn: iso, checkOut: filters.checkOut && iso && filters.checkOut < iso ? '' : filters.checkOut })}
                  minDate={new Date()}
                  placeholder="DD-MM-YYYY"
                  testId="check-in-input"
                />
              </div>
              <div className="hidden md:block w-px self-stretch bg-gray-200 my-2" />

              {/* Check-out */}
              <div className="flex-1 px-5 py-3 md:py-2 hover:bg-gray-50 rounded-2xl md:rounded-full transition-colors">
                <label className="text-[10px] font-bold uppercase tracking-wider text-gray-500 block mb-0.5">{t('hero.checkOut')}</label>
                <DatePicker
                  value={filters.checkOut}
                  onChange={(iso) => setFilters({ ...filters, checkOut: iso })}
                  minDate={filters.checkIn ? new Date(filters.checkIn + 'T00:00:00') : new Date()}
                  placeholder="DD-MM-YYYY"
                  testId="check-out-input"
                />
              </div>
              <div className="hidden md:block w-px self-stretch bg-gray-200 my-2" />

              {/* Guests */}
              <div className="flex-1 px-5 py-3 md:py-2 hover:bg-gray-50 rounded-2xl md:rounded-full transition-colors">
                <label className="text-[10px] font-bold uppercase tracking-wider text-gray-500 block mb-0.5 flex items-center gap-1">
                  <UsersIcon className="h-3 w-3" /> {t('hero.guests')}
                </label>
                <GuestPicker
                  adults={filters.adults}
                  children={filters.children}
                  onChange={({ adults, children }) => setFilters({ ...filters, adults, children })}
                  testIdPrefix="hero"
                />
              </div>

              {/* Search CTA */}
              <button
                onClick={handleSearch}
                className="bg-[#FF0059] hover:bg-[#CC0047] text-white font-bold px-8 py-4 rounded-2xl md:rounded-full transition-colors flex items-center justify-center gap-2 mt-2 md:mt-0 shadow-lg shadow-[#FF0059]/20"
                data-testid="search-button"
              >
                <Search className="h-5 w-5" /> {t('hero.search')}
              </button>
            </div>
          </div>

          {/* Amenity Quick Filters */}
          <div className="mt-6 flex flex-wrap gap-2 justify-center" data-testid="amenity-chips">
            {AMENITY_FILTERS.map((a) => (
              <button
                key={a.id}
                onClick={() => toggleAmenity(a.id)}
                className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-bold transition-all border-2 ${
                  selectedAmenities.includes(a.id)
                    ? 'bg-[#FF0059] text-white border-[#FF0059]'
                    : 'bg-white text-gray-700 border-gray-200 hover:border-[#FF0059] hover:text-[#FF0059]'
                }`}
              >
                {a.icon}
                {a.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* ========== WALLET HERO STRIP (logged-in guests only) ========== */}
      <WalletHeroStrip />

      {/* ========== PRICE-DROP SMS SUBSCRIBE ========== */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6" data-testid="price-drop-section">
        <PriceDropSubscribe />
      </section>

      {/* ========== TRENDING DESTINATIONS ========== */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16" data-testid="trending-section">
        <div className="flex items-end justify-between mb-8">
          <div>
            <h2 className="text-3xl sm:text-4xl font-black tracking-tight text-black">{t('section.trending')}</h2>
            <p className="text-gray-600 mt-2">{t('section.trendingSubtitle')}</p>
          </div>
          <button className="hidden md:inline-flex items-center gap-1 text-sm font-bold text-[#FF0059] hover:underline">
            {t('section.seeAll')} <ChevronRight className="h-4 w-4" />
          </button>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-5">
          {TRENDING_DESTINATIONS.map((d, idx) => (
            <button
              key={d.city}
              onClick={() => { setFilters({ ...filters, city: d.city, area: '' }); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
              className="relative rounded-2xl overflow-hidden group cursor-pointer aspect-[4/5] shadow-sm hover:shadow-xl transition-all"
              style={{ animationDelay: `${idx * 80}ms` }}
              data-testid={`destination-${d.city.toLowerCase()}`}
            >
              <img src={d.image} alt={d.city} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
              <div className="absolute bottom-4 left-4 right-4 text-left">
                <p className="text-xs font-bold text-white/80 uppercase tracking-wider">{d.state}</p>
                <h3 className="text-white font-black text-xl sm:text-2xl mt-0.5">{d.city}</h3>
                <p className="text-xs text-white/90 mt-1 line-clamp-1">{d.tagline}</p>
              </div>
            </button>
          ))}
        </div>
      </section>

      {/* ========== PROPERTY TYPES ========== */}
      <section className="bg-[#FAFAFA] py-16" data-testid="property-types-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-black tracking-tight text-black mb-2">{t('section.propertyTypes')}</h2>
          <p className="text-gray-600 mb-8">{t('section.propertyTypesSubtitle')}</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
            {propertyTypes.map((p) => {
              const Icon = PROPERTY_ICONS[p.icon] || HotelIcon;
              return (
                <button
                  key={p.id}
                  className="bg-white p-6 rounded-2xl border border-gray-200 hover:border-[#FF0059] hover:shadow-lg transition-all text-left group"
                  data-testid={`property-type-${p.id}`}
                >
                  <div className="w-12 h-12 rounded-full bg-[#FFE5EE] flex items-center justify-center group-hover:bg-[#FF0059] transition-colors mb-4">
                    <Icon className="h-6 w-6 text-[#FF0059] group-hover:text-white transition-colors" />
                  </div>
                  <h3 className="font-bold text-lg text-black">{p.label}</h3>
                  <p className="text-sm text-gray-500 mt-1">{p.count}+ properties</p>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* ========== ALL HOTELS / CITY RESULTS — surfaces FIRST when a city is selected ========== */}
      {filters.city && (
        <section ref={resultsRef} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 scroll-mt-20" data-testid="all-hotels-section-top">
          <div className="flex items-end justify-between mb-6">
            <div>
              <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#FFE5EE] text-[#FF0059] text-xs font-bold mb-2">
                <MapPin className="h-3.5 w-3.5" /> SEARCH RESULTS
              </div>
              <h2 className="text-2xl sm:text-3xl font-black tracking-tight text-black" data-testid="stays-in-city-title">
                {t('section.staysIn', { city: filters.city })}
              </h2>
              <p className="text-sm text-gray-600 mt-1">{t('section.properties', { count: hotels.length })}</p>
            </div>
            <button
              onClick={() => setFilters({ ...filters, city: '', area: '' })}
              className="text-xs font-bold text-[#FF0059] hover:underline"
              data-testid="clear-city-search"
            >
              Clear search ✕
            </button>
          </div>
          {loading ? (
            <div className="space-y-4">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="h-32 bg-gray-100 rounded-2xl animate-pulse" />
              ))}
            </div>
          ) : hotels.length === 0 ? (
            <div className="text-center py-16 bg-[#FAFAFA] rounded-2xl" data-testid="no-hotels-message-top">
              <p className="text-gray-600 font-bold">No hotels match your filters. Try removing some.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {hotels.map((hotel) => (
                <HotelListCard
                  key={hotel._id}
                  hotel={hotel}
                  onShare={shareHotel}
                  onOpenGallery={openGallery}
                  detailsUrl={buildHotelUrl(hotel._id)}
                />
              ))}
            </div>
          )}
        </section>
      )}

      {/* ========== TOP HOTELS ========== */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16" data-testid="top-hotels-section">
        <div className="flex items-end justify-between mb-8">
          <div>
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#FFF9DB] text-[#997800] text-xs font-bold mb-2">
              <Award className="h-3.5 w-3.5" /> {t('section.editorsChoice')}
            </div>
            <h2 className="text-3xl sm:text-4xl font-black tracking-tight text-black">{homeContent.section_top_hotels || t('section.topHotels')}</h2>
            <p className="text-gray-600 mt-2">{t('section.topHotelsSubtitle')}</p>
          </div>
        </div>
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="space-y-3 animate-pulse">
                <div className="aspect-[4/3] rounded-xl bg-gray-200" />
                <div className="h-4 bg-gray-200 rounded w-3/4" />
                <div className="h-3 bg-gray-200 rounded w-1/2" />
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {topHotels.map((hotel) => (
              <TopHotelCard
                key={hotel._id}
                hotel={hotel}
                detailsUrl={buildHotelUrl(hotel._id)}
                hasDates={Boolean(filters.checkIn && filters.checkOut)}
              />
            ))}
          </div>
        )}
      </section>

      {/* ========== ALL HOTELS — full list shown only when no city filter (city filter has its own top block) ========== */}
      {!filters.city && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 border-t border-gray-100" data-testid="all-hotels-section">
          <div className="flex items-end justify-between mb-6">
            <div>
              <h2 className="text-2xl sm:text-3xl font-black tracking-tight text-black">
                {t('section.allHotels')}
              </h2>
              <p className="text-sm text-gray-600 mt-1">{t('section.properties', { count: hotels.length })}</p>
            </div>
          </div>
          {hotels.length === 0 ? (
            <div className="text-center py-16 bg-[#FAFAFA] rounded-2xl" data-testid="no-hotels-message">
              <p className="text-gray-600 font-bold">No hotels match your filters. Try removing some.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {hotels.map((hotel) => (
                <HotelListCard
                  key={hotel._id}
                  hotel={hotel}
                  onShare={shareHotel}
                  onOpenGallery={openGallery}
                  detailsUrl={buildHotelUrl(hotel._id)}
                />
              ))}
            </div>
          )}
        </section>
      )}

      {/* ========== INSIGHTS / CITY GUIDES ========== */}
      <section className="bg-[#FAFAFA] py-16" data-testid="insights-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between mb-8">
            <div>
              <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#FFE5EE] text-[#FF0059] text-xs font-bold mb-2">
                <Sparkles className="h-3.5 w-3.5" /> {t('section.insightsTag')}
              </div>
              <h2 className="text-3xl sm:text-4xl font-black tracking-tight text-black">{t('section.insights')}</h2>
              <p className="text-gray-600 mt-2">{t('section.insightsSubtitle')}</p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {INSIGHTS.map((insight) => (
              <article
                key={insight.id}
                className="bg-white rounded-2xl overflow-hidden border border-gray-200 hover:shadow-lg transition-shadow cursor-pointer group"
                data-testid={`insight-${insight.id}`}
              >
                <div className="aspect-video overflow-hidden bg-gray-100">
                  <img src={insight.image} alt={insight.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-2 text-xs font-bold text-[#FF0059] mb-2">
                    {insight.type === 'trending' ? <TrendingUp className="h-3.5 w-3.5" /> : <MapPin className="h-3.5 w-3.5" />}
                    {insight.type === 'trending' ? 'TRENDING' : insight.city.toUpperCase()}
                    <span className="text-gray-400">·</span>
                    <span className="text-gray-500 font-medium">{insight.readTime}</span>
                  </div>
                  <h3 className="font-black text-lg text-black leading-tight mb-2 group-hover:text-[#FF0059] transition-colors line-clamp-2">
                    {insight.title}
                  </h3>
                  <p className="text-sm text-gray-600 line-clamp-3">{insight.excerpt}</p>
                  <button className="mt-4 inline-flex items-center gap-1 text-sm font-bold text-[#FF0059] hover:underline">
                    Read article <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ========== TESTIMONIALS ========== */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16" data-testid="testimonials-section">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#FFE5EE] text-[#FF0059] text-xs font-bold mb-3">
            <Quote className="h-3.5 w-3.5" /> {t('section.testimonialsTag')}
          </div>
          <h2 className="text-3xl sm:text-4xl font-black tracking-tight text-black">{t('section.testimonials')}</h2>
          <p className="text-gray-600 mt-2 max-w-2xl mx-auto">{t('section.testimonialsSubtitle')}</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {TESTIMONIALS.map((t) => (
            <div
              key={t.id}
              className="bg-[#FAFAFA] p-6 rounded-2xl border border-gray-200 flex flex-col gap-4 hover:border-[#FF0059] transition-colors"
              data-testid={`testimonial-card-${t.id}`}
            >
              <Quote className="h-7 w-7 text-[#FF0059] opacity-30" />
              <p className="text-black font-medium text-base leading-relaxed">{t.quote}</p>
              <div className="flex items-center gap-3 mt-auto pt-4 border-t border-gray-200">
                <img
                  src={t.avatar}
                  alt={t.name}
                  className="w-12 h-12 rounded-full object-cover bg-gray-200"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-sm text-black truncate">{t.name}</p>
                  <p className="text-xs text-gray-500 truncate">{t.role}</p>
                  <div className="flex gap-0.5 mt-0.5">
                    {[1, 2, 3, 4, 5].map((s) => (
                      <Star key={s} className={`h-3 w-3 ${s <= t.rating ? 'fill-[#FFCB00] text-[#FFCB00]' : 'text-gray-300'}`} />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ========== CTA BANNER ========== */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        <div className="rounded-3xl bg-gradient-to-br from-[#FF0059] to-[#CC0047] p-10 md:p-16 text-white text-center relative overflow-hidden" data-testid="cta-banner">
          <div className="absolute -top-20 -right-20 w-72 h-72 bg-[#FF4D8A] rounded-full opacity-20 blur-3xl" />
          <div className="relative">
            <h2 className="text-3xl sm:text-5xl font-black tracking-tight mb-3">{t('section.ownHotel')}</h2>
            <p className="text-base sm:text-lg text-white/90 mb-7 max-w-2xl mx-auto">
              {t('section.ownHotelSubtitle')}
            </p>
            <button
              onClick={() => navigate('/register', { state: { role: 'hotel' } })}
              className="bg-white text-[#FF0059] hover:bg-[#FF4D8A] hover:text-black font-black px-8 py-4 rounded-full transition-colors shadow-xl"
              data-testid="list-property-cta"
            >
              {t('section.listPropertyCta')}
            </button>
          </div>
        </div>
      </section>

      <ImageGallery
        images={galleryImages}
        hotelName={galleryHotelName}
        isOpen={isGalleryOpen}
        onClose={() => setIsGalleryOpen(false)}
      />
    </div>
  );
};
